import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { SearchInput } from '../common/SearchInput';
import {
  getWatchBrands,
  getWatchChartsBrands,
  mapWatchChartsBrand,
  type WatchChartsBrand,
} from '../../services/firebase';
import type { WatchBrand } from '../../types';
import { normalizeText } from '../../utils/text';

type RowStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * WatchCharts Brand Mapping (Phase A).
 *
 * Two-column screen: our brands (left) ↔ WatchCharts brands (right, dropdown).
 * Exact normalized-name matches are auto-selected as unsaved suggestions.
 * 1:1 is enforced client-side (a WatchCharts brand chosen in one row drops out
 * of every other row's options) and authoritatively server-side in
 * mapWatchChartsBrand.
 */
export function WatchChartsMapping() {
  const [ourBrands, setOurBrands] = useState<WatchBrand[]>([]);
  const [wcBrands, setWcBrands] = useState<WatchChartsBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  // brandId -> selected WatchCharts uuid ('' = not mapped)
  const [selections, setSelections] = useState<Record<string, string>>({});
  // brandId -> persisted (saved) uuid, the baseline for dirty detection
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});
  const [search, setSearch] = useState('');
  const [savingAll, setSavingAll] = useState(false);

  const load = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const [brands, wc] = await Promise.all([
        getWatchBrands(),
        getWatchChartsBrands(forceRefresh),
      ]);

      const sortedBrands = [...brands].sort((a, b) =>
        (a.displayName || a.name).localeCompare(b.displayName || b.name),
      );
      const sortedWc = [...wc.brands].sort((a, b) => a.name.localeCompare(b.name));
      setOurBrands(sortedBrands);
      setWcBrands(sortedWc);
      setLastFetchedAt(wc.lastFetchedAt);

      // Baseline from persisted mappings.
      const baseline: Record<string, string> = {};
      for (const b of sortedBrands) baseline[b.id] = b.watchChartsBrandUuid ?? '';
      setSaved(baseline);

      // Initial selections = baseline + auto-match suggestions for unmapped rows.
      const wcByNorm = new Map<string, WatchChartsBrand>();
      for (const w of sortedWc) {
        const key = normalizeText(w.name);
        if (!wcByNorm.has(key)) wcByNorm.set(key, w);
      }
      const taken = new Set(Object.values(baseline).filter(Boolean));
      const next: Record<string, string> = { ...baseline };
      for (const b of sortedBrands) {
        if (next[b.id]) continue; // already mapped
        const match = wcByNorm.get(normalizeText(b.name));
        if (match && !taken.has(match.uuid)) {
          next[b.id] = match.uuid;
          taken.add(match.uuid); // don't auto-match two of our brands to the same WC brand
        }
      }
      setSelections(next);
      setRowStatus({});
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not load WatchCharts brands. The API may be unavailable.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const wcByUuid = useMemo(() => {
    const m = new Map<string, WatchChartsBrand>();
    for (const w of wcBrands) m.set(w.uuid, w);
    return m;
  }, [wcBrands]);

  // Exact normalized-name auto-match per brand (for the "Auto-matched" badge).
  const autoMatchByBrand = useMemo(() => {
    const wcByNorm = new Map<string, WatchChartsBrand>();
    for (const w of wcBrands) {
      const key = normalizeText(w.name);
      if (!wcByNorm.has(key)) wcByNorm.set(key, w);
    }
    const m = new Map<string, string>();
    for (const b of ourBrands) {
      const match = wcByNorm.get(normalizeText(b.name));
      if (match) m.set(b.id, match.uuid);
    }
    return m;
  }, [ourBrands, wcBrands]);

  // uuid -> brandId currently selecting it (for 1:1 client enforcement).
  const uuidOwner = useMemo(() => {
    const m = new Map<string, string>();
    for (const [brandId, uuid] of Object.entries(selections)) {
      if (uuid) m.set(uuid, brandId);
    }
    return m;
  }, [selections]);

  const dirtyIds = useMemo(
    () => ourBrands.filter((b) => (selections[b.id] ?? '') !== (saved[b.id] ?? '')).map((b) => b.id),
    [ourBrands, selections, saved],
  );

  const mappedCount = useMemo(
    () => Object.values(saved).filter(Boolean).length,
    [saved],
  );

  const filteredBrands = useMemo(() => {
    const q = normalizeText(search.trim());
    if (!q) return ourBrands;
    return ourBrands.filter(
      (b) =>
        normalizeText(b.displayName || b.name).includes(q) ||
        normalizeText(b.id).includes(q),
    );
  }, [ourBrands, search]);

  const setSelection = (brandId: string, uuid: string) => {
    setSelections((prev) => ({ ...prev, [brandId]: uuid }));
    setRowStatus((prev) => ({ ...prev, [brandId]: 'idle' }));
  };

  const saveChanges = async () => {
    if (dirtyIds.length === 0) return;
    setSavingAll(true);
    let ok = 0;
    let failed = 0;
    // Sequential to avoid contention on the server-side 1:1 transaction.
    for (const brandId of dirtyIds) {
      const uuid = selections[brandId] || null;
      const name = uuid ? wcByUuid.get(uuid)?.name ?? null : null;
      setRowStatus((prev) => ({ ...prev, [brandId]: 'saving' }));
      try {
        await mapWatchChartsBrand(brandId, uuid, name);
        setSaved((prev) => ({ ...prev, [brandId]: uuid ?? '' }));
        setRowStatus((prev) => ({ ...prev, [brandId]: 'saved' }));
        ok += 1;
      } catch (err) {
        failed += 1;
        setRowStatus((prev) => ({ ...prev, [brandId]: 'error' }));
        const brand = ourBrands.find((b) => b.id === brandId);
        const msg = err instanceof Error ? err.message : 'Save failed';
        toast.error(`${brand?.displayName || brand?.name || brandId}: ${msg}`);
      }
    }
    setSavingAll(false);
    if (ok > 0 && failed === 0) toast.success(`Saved ${ok} mapping${ok === 1 ? '' : 's'}`);
    else if (ok > 0) toast(`Saved ${ok}, ${failed} failed`);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-gray-500 py-12 justify-center">
        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Loading brands…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <h3 className="text-sm font-semibold text-red-800">WatchCharts API unavailable</h3>
        <p className="mt-1 text-sm text-red-700">{error}</p>
        <div className="mt-4">
          <Button variant="danger" size="sm" onClick={() => void load(true)}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">WatchCharts Brand Mapping</h2>
          <p className="text-sm text-gray-500 mt-1">
            Map each of our brands to its WatchCharts brand (1:1). Exact name matches are
            auto-selected but not saved until you click Save.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {mappedCount} of {ourBrands.length} brands mapped
            {dirtyIds.length > 0 && ` • ${dirtyIds.length} unsaved change${dirtyIds.length === 1 ? '' : 's'}`}
            {lastFetchedAt && ` • brand list synced ${new Date(lastFetchedAt).toLocaleString()}`}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => void load(true)} disabled={savingAll}>
            Refresh list
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void saveChanges()}
            loading={savingAll}
            disabled={dirtyIds.length === 0 || savingAll}
          >
            Save{dirtyIds.length > 0 ? ` (${dirtyIds.length})` : ''}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-3 max-w-sm">
        <SearchInput
          placeholder="Filter our brands…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
        />
      </div>

      {/* Two-column table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-4 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wide">
          <div>Our brand</div>
          <div>WatchCharts brand</div>
          <div className="text-right">Status</div>
        </div>
        <div className="divide-y divide-gray-100">
          {filteredBrands.map((brand) => {
            const selected = selections[brand.id] ?? '';
            const baseline = saved[brand.id] ?? '';
            const dirty = selected !== baseline;
            const status = rowStatus[brand.id] ?? 'idle';
            const autoUuid = autoMatchByBrand.get(brand.id);
            const isAutoSuggested = !baseline && !!autoUuid && selected === autoUuid && dirty;

            // 1:1 — options = brands not taken by another row, plus the current selection.
            const options = wcBrands.filter((w) => {
              const owner = uuidOwner.get(w.uuid);
              return !owner || owner === brand.id;
            });

            return (
              <div
                key={brand.id}
                className="grid grid-cols-[1fr_1fr_auto] gap-4 px-4 py-2.5 items-center"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {brand.displayName || brand.name}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{brand.id}</div>
                </div>

                <div className="flex items-center gap-2 min-w-0">
                  <select
                    value={selected}
                    onChange={(e) => setSelection(brand.id, e.target.value)}
                    className="block w-full text-sm border border-gray-300 rounded-lg py-1.5 px-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
                  >
                    <option value="">— Not mapped —</option>
                    {options.map((w) => (
                      <option key={w.uuid} value={w.uuid}>
                        {w.name}
                        {typeof w.watch_count === 'number' ? ` (${w.watch_count})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="text-right text-xs whitespace-nowrap">
                  {status === 'saving' && <span className="text-gray-400">Saving…</span>}
                  {status === 'error' && <span className="text-red-600 font-medium">Failed</span>}
                  {status !== 'saving' && status !== 'error' && (
                    <>
                      {isAutoSuggested && (
                        <span className="inline-block bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          Auto-matched
                        </span>
                      )}
                      {!isAutoSuggested && dirty && (
                        <span className="inline-block bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          Unsaved
                        </span>
                      )}
                      {!dirty && baseline && (
                        <span className="inline-block bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          Mapped
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {filteredBrands.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-gray-400">No brands match your filter.</div>
          )}
        </div>
      </div>
    </div>
  );
}
